import { Users } from "lucide-react";
import { usePresence } from "../../hooks/usePresence";
import { cn } from "../../lib/utils";

const COLORS = [
  "bg-(--accent-500)",
  "bg-yellow-500",
  "bg-pink-500",
  "bg-blue-500",
  "bg-purple-500",
];

/**
 * PresenceIndicator — shows up to 5 reader avatars in the viewer header
 * and "+N" pill for the rest. Excludes the current user (server filters
 * via DocAgent presence map, but we also defensively dedup here).
 */
export function PresenceIndicator({
  docId,
  selfSubject,
}: {
  docId: string;
  selfSubject: string | null;
}) {
  const { data } = usePresence(docId);
  const readers = (data?.items ?? []).filter((p) => p.subject !== selfSubject);
  if (readers.length === 0) return null;

  const visible = readers.slice(0, 5);
  const extra = readers.length - visible.length;

  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full bg-(--bg-base) px-2 py-0.5 text-xs"
      data-testid="presence-indicator"
      title={readers.map((r) => r.displayName ?? r.subject).join(", ")}
    >
      <Users size={11} className="text-(--text-tertiary)" />
      <span className="flex -space-x-1.5">
        {visible.map((r, i) => (
          <span
            key={r.subject}
            className={cn(
              "grid h-5 w-5 place-items-center rounded-full ring-1 ring-(--bg-base) text-[10px] font-medium text-white",
              COLORS[i % COLORS.length],
            )}
            title={r.displayName ?? r.subject}
          >
            {initials(r.displayName ?? r.subject)}
          </span>
        ))}
      </span>
      {extra > 0 && <span className="text-[10px] text-(--text-tertiary)">+{extra}</span>}
    </div>
  );
}

function initials(name: string): string {
  const trimmed = name
    .replace(/@.*$/, "")
    .replace(/[._-]+/g, " ")
    .trim();
  return (
    trimmed
      .split(/\s+/)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .slice(0, 2)
      .join("") || "?"
  );
}
